import React, { useState, useEffect } from "react";
import MAuActionBar from "./MAuActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";
import { Upload } from "lucide-react";

const MAu = () => {
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mau/maus`;
  const { role } = useSelector((state) => state.auth);

  const columns = [
    { key: "UnitID", label: "Unit ID" },
    { key: "UnitName", label: "Unit Name" },
    { key: "Description", label: "Description / Remarks" },
  ];

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/x-vnd.oasis.opendocument.spreadsheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      setImportError("Please upload a valid CSV or ODS file");
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setImportError("");

    try {
      const response = await axios.post(`${apiEndpoint}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data.message) {
        alert(response.data.message);
        await fetchUnits(1);
      }
    } catch (error) {
      console.error("Import error:", error);
      setImportError(error.response?.data?.error || "Error importing file");
    } finally {
      setImporting(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const fetchUnits = async (page = currentPage, query = searchQuery) => {
    try {
      const response = await axios.get(
        `${apiEndpoint}?page=${page}&limit=${itemsPerPage}&search=${query}`
      );
      setUnits(response.data.items);
      setFilteredUnits(response.data.items);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setTotalItems(response.data.total);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUnits(page);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    fetchUnits(1, query);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedRow) {
        await axios.put(`${apiEndpoint}/${selectedRow.UnitID}`, formData);
      } else {
        await axios.post(apiEndpoint, formData);
      }
      fetchUnits(currentPage);
      setSelectedRow(null);
    } catch (error) {
      console.error("Error saving unit:", error);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await axios.delete(`${apiEndpoint}/${selectedRow.UnitID}`);
      fetchUnits(currentPage);
      setSelectedRow(null);
    } catch (error) {
      console.error("Error deleting unit:", error);
    }
  };


  useEffect(() => {
    fetchUnits(currentPage, searchQuery);
  }, [currentPage]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header exportEndpoint={apiEndpoint} />

      <div className="flex-grow p-4">
        <div className="flex justify-between items-center mb-4">
          <MAuActionBar
            title="Accounting Unit"
            rowKey="UnitID"
            deleteMessage="Are you sure you want to remove this location permanently?"
            fields={[
              { name: "UnitName", label: "Unit", required: true },
              {
                name: "Description",
                label: "Description / Remarks",
                type: "Text",
              },
            ]}
            apiEndpoint={apiEndpoint}
            data={filteredUnits}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={() => fetchUnits(currentPage)}
            onSearch={handleSearch}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            userRole={role}
          />

          <div className="flex items-center">
            <label className="relative cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center">
              <Upload className="mr-2" size={20} />
              {importing ? "Importing..." : "Import CSV/ODS"}
              <input
                type="file"
                className="hidden"
                accept=".csv,.ods"
                onChange={handleFileImport}
                disabled={importing}
              />
            </label>
            {importError && (
              <div className="text-red-500 ml-2">{importError}</div>
            )}
          </div>
        </div>

        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Units Table</h3>
        </div>

        <div className="mt-4">
          <DynamicTable
            columns={columns}
            data={filteredUnits}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            rowKey="UnitID"
            highlightColor="bg-green-100"
            noDataMessage="No units found."
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MAu;